import type { TreeViewItemType } from '#components/treeView';
import type { FiltersTreeType } from '#composables/kpisPerimeter';
import { useLanguage } from '#composables/useLanguage';
import {
  CUSTOMER_SEGMENTATION_GRANULARITY_VIEWS,
  CUSTOMER_SEGMENTATION_TYPES,
  compareCustomerSegmentByKey,
  type CustomerSegmentationType
} from '@carrefour-gcs/shared';
import { useQuery } from '@tanstack/vue-query';
import { computed, unref, type ComputedRef, type Ref } from 'vue';
import { findCustomerSegmentationSegments } from '../../filter.query';

const { CONSOSTYLE, LIFESTAGE } = CUSTOMER_SEGMENTATION_TYPES;
const { MICRO } = CUSTOMER_SEGMENTATION_GRANULARITY_VIEWS;

interface UseCustomerSegmentationFilterDataParams {
  debouncedSearch: Readonly<Ref<string>>;
  enabled: ComputedRef<boolean>;
  checkedItemsState: Ref<FiltersTreeType>;
  segmentationType: CustomerSegmentationType;
}

export function useCustomerSegmentationFilterData({
  debouncedSearch,
  enabled,
  checkedItemsState,
  segmentationType
}: UseCustomerSegmentationFilterDataParams) {
  const { t } = useLanguage();

  const { data, isLoading } = useQuery({
    enabled,
    queryKey: ['customerSegmentationSegments', segmentationType],
    queryFn: () =>
      findCustomerSegmentationSegments({
        segmentationType: segmentationType,
        limit: 100
      })
  });

  const items = computed<TreeViewItemType | undefined>(() => {
    if (!data.value || data.value.status === 'error') {
      return undefined;
    }

    const selectedFilters =
      segmentationType === CONSOSTYLE
        ? checkedItemsState.value.consostyleMicroFilters || {}
        : checkedItemsState.value.lifestageMicroFilters || {};

    const microType = `${segmentationType}Micro` as const;
    const search = unref(debouncedSearch).toLowerCase();

    const sortedSegments = data.value.data.children
      ? [...data.value.data.children].sort((a, b) =>
          compareCustomerSegmentByKey(a.label, b.label, segmentationType, MICRO)
        )
      : [];

    const segmentTypePattern = new RegExp(`customerSegmentationType=(${CONSOSTYLE}|${LIFESTAGE})-`);

    const children = sortedSegments
      .map((segment) => {
        // Convert API format (customerSegmentationType=consostyle-3) to state format (consostyleMicro=3)
        const nodeId = segment.nodeId.replace(segmentTypePattern, '$1Micro=');
        const label = t(`customerSegmentation.segments.${segmentationType}.${MICRO}.${segment.label}`);

        return {
          filterKey: segment.filterKey,
          label,
          type: microType,
          checkedStatus: selectedFilters[nodeId]?.state ?? 'unchecked',
          children: [],
          nodeId,
          disabled: false,
          disabledMessage: undefined
        };
      })
      .filter((child) => !search || child.label.toLowerCase().includes(search));

    return {
      filterKey: microType,
      label: microType,
      type: microType,
      checkedStatus: 'unchecked' as const,
      children: children || [],
      nodeId: `${microType}-root`,
      disabled: false,
      disabledMessage: undefined
    };
  });

  const isLoadingFilters = computed(() => isLoading.value);

  return {
    items,
    isLoadingFilters
  };
}
